import React, { useState, useEffect } from "react";
import { Alert, Modal, ModalHeader, ModalBody, Badge } from "reactstrap";
import { useSelector } from "react-redux";

const VERSION_URL = "https://www.ticketcontrol.net/version.json";
const CRITICAL_VERSION = "1.2.176";
const UPGRADE_EXE_VERSION = "1.2.141";
const WHATSAPP_PHONE = "542236769606";
const WHATSAPP_MESSAGE = "Hola ! necesito ayuda para actualizar TicketControl.";

function compareVersions(v1, v2) {
  if (!v1 || !v2) return 0;
  const a = v1.split(".").map(Number);
  const b = v2.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const n1 = a[i] || 0;
    const n2 = b[i] || 0;
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
  }
  return 0;
}

const HowToUpdateModal = ({ isOpen, toggle, hasUpgradeExe }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered scrollable>
    <ModalHeader toggle={toggle}>
      <span
        className="material-symbols-rounded me-2"
        style={{ fontSize: 22, verticalAlign: "middle" }}
      >
        help
      </span>
      Cómo actualizar TicketControl
    </ModalHeader>
    <ModalBody>
      {hasUpgradeExe ? (
        <div>
          <div className="d-flex align-items-start mb-2">
            <span
              className="material-symbols-rounded me-2 text-success flex-shrink-0"
              style={{ fontSize: 22, marginTop: 2 }}
            >
              system_update_alt
            </span>
            <div>
              <h6 className="mb-2">Actualización automática</h6>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                En la PC del estacionamiento, abrí <strong>TicketControl</strong> y
                verás un botón <strong>"Actualizar"</strong> en la parte inferior de la pantalla.
                Solo tenés que presionarlo.
              </p>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                El programa se cerrará automáticamente, descargará la nueva versión
                y se abrirá el instalador. Seguí los pasos hasta el final.
                Luego, abrí TicketControl normalmente y listo.
              </p>
              <Alert color="success" className="py-2 px-3 mb-0" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>
                  verified
                </span>
                <strong>Tu información no se pierde.</strong> Todos tus tickets, configuraciones,
                cuentas corrientes y datos permanecen intactos después de la actualización.
              </Alert>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="d-flex align-items-start mb-3">
            <span
              className="material-symbols-rounded me-2 text-warning flex-shrink-0"
              style={{ fontSize: 22, marginTop: 2 }}
            >
              support_agent
            </span>
            <div>
              <h6 className="mb-2">Necesitás ayuda de Soporte</h6>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                La versión instalada en tu estacionamiento es muy antigua y no cuenta
                con el sistema de actualización automática. Nuestro equipo de soporte
                te ayudará a actualizar de forma segura sin perder tu información.
              </p>
            </div>
          </div>
          <a
            href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
          >
            <i className="ri-whatsapp-line" style={{ fontSize: 18 }}></i>
            Contactar a Soporte por WhatsApp
          </a>
        </div>
      )}
    </ModalBody>
  </Modal>
);

const VersionBanner = ({ isCritical, currentVersion, latestVersion, newsSince, onShowModal, onShowHowToUpdate }) => (
  <Alert
    color={isCritical ? "danger" : "warning"}
    className="mb-3"
    style={{ borderRadius: 8 }}
  >
    <div className="d-flex align-items-start">
      <span
        className="material-symbols-rounded me-2 flex-shrink-0"
        style={{ fontSize: 22, marginTop: 1 }}
      >
        {isCritical ? "error" : "system_update"}
      </span>
      <div className="flex-grow-1">
        {isCritical ? (
          <>
            <strong>Actualización requerida en el Estacionamiento</strong>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              El software TicketControl instalado en el estacionamiento
              (v{currentVersion}) es muy antiguo. Se recomienda actualizar
              a la versión <strong>{latestVersion}</strong> lo antes posible.
              Las versiones recientes incluyen mejoras importantes de
              rendimiento, seguridad y nuevas funcionalidades.
            </div>
          </>
        ) : (
          <>
            <strong>Nueva versión disponible para el Estacionamiento</strong>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              Hay una nueva versión de TicketControl disponible (
              <strong>{latestVersion}</strong>) para actualizar en el
              estacionamiento. Las actualizaciones suelen incluir mejoras de
              rendimiento y nuevas funciones. Versión instalada: {currentVersion}
            </div>
          </>
        )}
        <div className="d-flex align-items-center gap-3 mt-1 flex-wrap">
          {newsSince.length > 0 && (
            <button
              className="btn btn-link p-0"
              style={{ fontSize: 13, textDecoration: "none" }}
              onClick={onShowModal}
            >
              Ver novedades
              <span
                className="material-symbols-rounded ms-1"
                style={{ fontSize: 14, verticalAlign: "middle" }}
              >
                arrow_forward
              </span>
            </button>
          )}
          <button
            className="btn btn-link p-0"
            style={{ fontSize: 13, textDecoration: "none" }}
            onClick={onShowHowToUpdate}
          >
            <span
              className="material-symbols-rounded me-1"
              style={{ fontSize: 14, verticalAlign: "middle" }}
            >
              help
            </span>
            Cómo actualizar
          </button>
        </div>
      </div>
    </div>
  </Alert>
);

const WhatsNewModal = ({ isOpen, toggle, currentVersion, latestVersion, newsSince }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered scrollable size="lg">
    <ModalHeader toggle={toggle}>
      <span
        className="material-symbols-rounded me-2"
        style={{ fontSize: 22, verticalAlign: "middle" }}
      >
        new_releases
      </span>
      Novedades de TicketControl
    </ModalHeader>
    <ModalBody>
      <p className="text-muted mb-3" style={{ fontSize: 13 }}>
        Estas son las novedades desde tu versión actual ({currentVersion}) hasta
        la última versión ({latestVersion}).
      </p>
      {newsSince.map((release) => (
        <div key={release.version} className="mb-4">
          <h6 className="d-flex align-items-center gap-2 mb-2">
            <Badge
              color={release.version === latestVersion ? "success" : "secondary"}
              style={{ fontSize: 12 }}
            >
              v{release.version}
            </Badge>
            {release.version === latestVersion && (
              <span className="text-success" style={{ fontSize: 11, fontWeight: 600 }}>
                ÚLTIMA
              </span>
            )}
          </h6>
          {(release.details || []).map((detail, idx) => (
            <div
              key={idx}
              className="ms-2 mb-2 ps-2"
              style={{ borderLeft: "2px solid #e9e9ef" }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{detail.title}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{detail.description}</div>
            </div>
          ))}
        </div>
      ))}
    </ModalBody>
  </Modal>
);

const VersionAlert = () => {
  const versionDB = useSelector((state) => state.data.versionDB);
  const [remoteData, setRemoteData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showHowToUpdate, setShowHowToUpdate] = useState(false);

  useEffect(() => {
    if (!versionDB || versionDB === "0.0.0") return;
    fetch(VERSION_URL)
      .then((res) => res.json())
      .then((data) => setRemoteData(data))
      .catch(() => {});
  }, [versionDB]);

  if (!remoteData || !versionDB || versionDB === "0.0.0") return null;

  const latest = remoteData.version;
  const isOutdated = compareVersions(versionDB, latest) < 0;
  if (!isOutdated) return null;

  const isCritical = compareVersions(versionDB, CRITICAL_VERSION) < 0;

  // Las novedades van acotadas POR ARRIBA a `latest`, no solo por abajo a la version
  // instalada. Sin ese tope, `version.json` mostraba novedades de versiones que
  // todavia no habian salido —tiene entradas de 2.0.71 y 2.0.67 mientras `version`
  // declara 2.0.63— y a un cliente en 2.0.60 se le prometian funciones que NO iba a
  // recibir al actualizar. Ademas el cartel "ULTIMA" quedaba sobre 2.0.63 con dos
  // versiones mas altas listadas encima.
  //
  // La regla es: se muestra exactamente lo que el cliente va a tener al actualizar, o
  // sea hasta la version declarada al principio del archivo. Es lo mismo que muestra
  // el Desktop.
  //
  // Va en el codigo y no solo en los datos porque asi no depende de acordarse de no
  // publicar novedades adelantadas: el archivo puede tenerlas cargadas de antemano
  // —para una beta, por ejemplo— sin que se filtren a produccion.
  const newsSince = (remoteData.news || []).filter(
    (n) =>
      compareVersions(n.version, versionDB) > 0 &&
      compareVersions(n.version, latest) <= 0
  );

  const hasUpgradeExe = compareVersions(versionDB, UPGRADE_EXE_VERSION) >= 0;

  return (
    <>
      <VersionBanner
        isCritical={isCritical}
        currentVersion={versionDB}
        latestVersion={latest}
        newsSince={newsSince}
        onShowModal={() => setShowModal(true)}
        onShowHowToUpdate={() => setShowHowToUpdate(true)}
      />
      <WhatsNewModal
        isOpen={!!showModal}
        toggle={() => setShowModal(false)}
        currentVersion={versionDB}
        latestVersion={latest}
        newsSince={newsSince}
      />
      <HowToUpdateModal
        isOpen={!!showHowToUpdate}
        toggle={() => setShowHowToUpdate(false)}
        hasUpgradeExe={hasUpgradeExe}
      />
    </>
  );
};

export default VersionAlert;
